/**
 * 키워드 파싱 알고리즘
 *
 * 지원 패턴:
 * - "서울 갤럭시" → 서울 지역 + 갤럭시 상품
 * - "갤럭시 서울" → 동일 (순서 무관)
 * - "갤럭시" → 전체 지역에서 갤럭시 상품
 * - "서울" → 서울 지역 전체 상품
 * - "강남 아이폰" → 강남구 + 아이폰 상품
 * - "홍대 카메라" → 홍대 랜드마크 + 카메라 카테고리
 */

import { COUNTRIES, getCityById } from "@/config/locations";
import { resolvePlace } from "@/lib/search/place-resolver";
import type { Locale } from "@/i18n/routing";
// 카테고리 매칭은 더 이상 사용하지 않음 (상품명 검색으로 대체)

export type LocationType = "city" | "district" | "landmark";
export type SupportedLocale = Locale;

export interface ParsedLocation {
  type: LocationType;
  id: string;
  name: string;
  coordinates: { latitude: number; longitude: number };
  searchRadius: number; // 검색 반경 (km)
  cityId?: string; // 소속 도시 ID (district, landmark의 경우)
}

// 검색 타입: 지도 표시 여부 결정에 사용
export type SearchType = "location" | "product" | "combined";

export interface ParsedKeyword {
  location: ParsedLocation | null;
  productKeywords: string[];
  originalQuery: string;
  hasLocationMatch: boolean;
  hasProductKeyword: boolean;
  searchType: SearchType; // 검색 타입 (location/product/combined)
}

/**
 * 토큰이 지명인지 확인하고 ParsedLocation으로 변환한다.
 *
 * 해석은 통합 리졸버(place-resolver)에 위임한다. 예전에는 여기서 districts(58개) +
 * CITIES만 봤기 때문에, 서버가 SEARCH_PLACES·STATION_PLACES까지 1,300여 개를 아는
 * 동안 검색바는 "성수"를 상품명으로 분류했다. 두 경로가 같은 사전을 보게 하는 것이
 * 이 위임의 목적이다.
 *
 * 국가는 여기서 다루지 않는다 — 좌표가 아니라 delivery_country 필터로 라우팅되므로
 * SearchBar가 matchCountryByName으로 따로 처리한다.
 */
function matchLocation(
  token: string,
  locale: SupportedLocale,
  opts?: { preferCityId?: string },
): ParsedLocation | null {
  const place = resolvePlace(token, opts);
  if (!place?.center || place.kind === "country") return null;

  // SearchBar는 city와 그 외(구·랜드마크·수기지명·역)를 다르게 라우팅한다. 도시는
  // 도시 슬러그 + getCityBounds, 나머지는 상위 도시 슬러그 + 좌표 기반 bounds.
  const type: LocationType =
    place.kind === "city"
      ? "city"
      : place.kind === "landmark"
        ? "landmark"
        : "district";

  // 도시는 표시명이 bounds 조회 키로도 쓰이므로 로케일 표기를 정확히 준다.
  const city = place.kind === "city" ? getCityById(place.id) : undefined;
  const name = city
    ? city.name[locale] || city.name.en || city.name.ko
    : place.label;

  return {
    type,
    id: place.id,
    name,
    coordinates: { latitude: place.center.lat, longitude: place.center.lng },
    searchRadius: place.radiusKm ?? 5,
    cityId: place.cityId,
  };
}

/**
 * 검색 키워드 파싱 (메인 함수)
 *
 * 단순화된 로직:
 * - 위치 매칭만 수행 (카테고리 매칭 제거)
 * - 위치가 아닌 모든 토큰은 productKeywords로 분류
 * - searchType 자동 결정: location/product/combined
 *
 * @param query - 검색어 (예: "서울 카메라", "강남 갤럭시")
 * @param locale - 언어 코드
 * @returns ParsedKeyword 객체
 */
export interface CountryMatch {
  /** ISO-2 country code (KR/JP/…) */
  countryCode: string;
  /** 국가명을 제거하고 남은 상품 키워드 (없으면 "") */
  keyword: string;
}

/**
 * 쿼리 맨 앞의 국가명을 인식한다 (모든 언어 이름으로 매칭 — locale 무관).
 * 예) locale=ko 라도 "japan galaxy" → {JP, "galaxy"}, "대한민국 유모차" → {KR, "유모차"},
 *     "일본" → {JP, ""}, "日本 galaxy" → {JP, "galaxy"}.
 * 국가명이 쿼리의 시작과 정확히 일치할 때만 매칭하므로 오탐이 낮다.
 * 도시/구군/랜드마크 매칭(parseSearchKeyword)과 별개로, 국가는 좌표가 아니라
 * delivery_country 필터로 라우팅되기 때문에 분리한다.
 */
export function matchCountryByName(query: string): CountryMatch | null {
  const trimmed = query.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();

  for (const country of COUNTRIES) {
    for (const name of Object.values(country.name)) {
      if (!name) continue;
      const n = name.toLowerCase();
      if (lower === n) {
        return { countryCode: country.code, keyword: "" };
      }
      if (lower.startsWith(`${n} `)) {
        return {
          countryCode: country.code,
          keyword: trimmed.slice(name.length).trim(),
        };
      }
    }
  }
  return null;
}

export function parseSearchKeyword(
  query: string,
  locale: SupportedLocale = "ko",
  opts?: { preferCityId?: string },
): ParsedKeyword {
  const originalQuery = query.trim();
  const tokens = originalQuery.split(/\s+/).filter(Boolean);

  let location: ParsedLocation | null = null;
  const productKeywords: string[] = [];

  // 단일 패스: 위치 매칭만 수행, 나머지는 모두 productKeywords
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];

    // 위치 매칭 시도 (아직 위치가 없으면)
    if (!location) {
      const locationMatch = matchLocation(token, locale, opts);
      if (locationMatch) {
        location = locationMatch;
        continue;
      }
    }

    // 위치가 아닌 토큰은 상품 키워드로
    productKeywords.push(token);
  }

  // 검색 타입 결정
  const hasLocation = location !== null;
  const hasProduct = productKeywords.length > 0;

  let searchType: SearchType;
  if (hasLocation && !hasProduct) {
    searchType = "location";   // 지역만 → 지도 표시
  } else if (!hasLocation && hasProduct) {
    searchType = "product";    // 상품만 → 지도 숨김
  } else {
    searchType = "combined";   // 지역+상품 → 지도 표시
  }

  return {
    location,
    productKeywords,
    originalQuery,
    hasLocationMatch: hasLocation,
    hasProductKeyword: hasProduct,
    searchType,
  };
}

/**
 * 검색 API 파라미터 인터페이스
 */
export interface SearchParams {
  // 위치 기반 검색
  latitude?: number;
  longitude?: number;
  radius?: number;
  location?: string; // 지역명 (fallback)

  // 카테고리 필터
  category_code?: string;

  // 상품명 검색
  search?: string;

  // 이용 방식
  usageMethod?: "pickup" | "delivery" | "shipping" | "all";

  // 페이지네이션
  limit?: number;
  offset?: number;

  // 관련 상품 포함 (결과 없을 때)
  includeRelated?: boolean;
}

/**
 * ParsedKeyword를 API 파라미터로 변환
 */
export function buildSearchParams(
  parsed: ParsedKeyword,
  options?: {
    usageMethod?: "pickup" | "delivery" | "shipping" | "all";
    limit?: number;
    offset?: number;
    includeRelated?: boolean;
  }
): SearchParams {
  const params: SearchParams = {};

  // 위치 기반 검색
  if (parsed.location) {
    params.latitude = parsed.location.coordinates.latitude;
    params.longitude = parsed.location.coordinates.longitude;
    params.radius = parsed.location.searchRadius;
    params.location = parsed.location.name;
  }

  // 상품명 검색 (카테고리 매칭 제거됨)
  if (parsed.productKeywords.length > 0) {
    params.search = parsed.productKeywords.join(" ");
  }

  // 추가 옵션
  if (options?.usageMethod) {
    params.usageMethod = options.usageMethod;
  }
  if (options?.limit) {
    params.limit = options.limit;
  }
  if (options?.offset) {
    params.offset = options.offset;
  }
  if (options?.includeRelated) {
    params.includeRelated = options.includeRelated;
  }

  return params;
}

/**
 * 검색 결과가 없을 때 대안 검색 생성
 */
export interface FallbackSearch {
  type: "location_only" | "keyword_only" | "all";
  params: SearchParams;
  description: string;
}

export function generateFallbackSearches(
  parsed: ParsedKeyword
): FallbackSearch[] {
  const fallbacks: FallbackSearch[] = [];

  // 1. 위치만으로 검색 (키워드 제외)
  if (parsed.location) {
    fallbacks.push({
      type: "location_only",
      params: {
        latitude: parsed.location.coordinates.latitude,
        longitude: parsed.location.coordinates.longitude,
        radius: parsed.location.searchRadius,
        location: parsed.location.name,
      },
      description: `'${parsed.location.name}' 지역의 다른 상품`,
    });
  }

  // 2. 상품 키워드만으로 검색 (전체 지역)
  if (parsed.productKeywords.length > 0) {
    const keyword = parsed.productKeywords.join(" ");
    fallbacks.push({
      type: "keyword_only",
      params: {
        search: keyword,
      },
      description: `전체 지역의 '${keyword}' 상품`,
    });
  }

  return fallbacks;
}

/**
 * 검색어 분석 결과를 사람이 읽을 수 있는 형태로 변환
 * (내부 로깅/디버깅 용도)
 */
export function describeSearchKeyword(parsed: ParsedKeyword): string {
  const parts: string[] = [];

  if (parsed.location) {
    parts.push(`지역: ${parsed.location.name} (${parsed.location.type})`);
  }

  if (parsed.productKeywords.length > 0) {
    parts.push(`키워드: ${parsed.productKeywords.join(", ")}`);
  }

  parts.push(`타입: ${parsed.searchType}`);

  if (parts.length === 1) {
    return `전체 검색: "${parsed.originalQuery}"`;
  }

  return parts.join(" | ");
}
